import type { Lead } from '@/types/crm'

/**
 * Where this company stands, and who has it — the working strip.
 *
 * ## What it stopped repeating
 *
 * It used to open with a 48px logo and the company name, then a meta line of
 * region, website and socials, then a stage pill, a score pill and an owner
 * select. Expanded inside the table, **every one of those was already on the row
 * directly above it** — the identity cell is the same logo and name over a
 * `region · website` subtitle, and the STAGE, AUTO SCORE, COUNTRY and OWNER
 * columns carry the rest. Opening a lead repeated the line you clicked on.
 *
 * Removed in two passes, 4 Sep 2026: first the logo and the name, then the
 * facts that are columns. The rule this settles on is worth stating plainly:
 *
 * > **The row is authoritative for anything a column can show. This panel shows
 * > what a column cannot.**
 *
 * So what is left is the two signals that have no column: the consumer-Skida
 * awareness badge, and whether somebody at the company already holds an
 * account.
 *
 * Where the removed things went: the stage is the `ProcessStepper` below, which
 * is a control rather than a label; the owner is a field in `LeadFields`, with
 * the rest of the editing; the score, the country and the estimated value are
 * columns and nothing else. The site and social links moved into `LeadFields`
 * too, under the boxes that set them — a link and the field that produces it
 * belong next to each other rather than a card apart.
 */
export function LeadIdentity({
  lead,
  awareness,
}: {
  lead: Lead
  awareness: { label: string; color: string; bg: string } | null
}) {
  /**
   * Nothing to say, so nothing rendered.
   *
   * Both pills are optional, so a lead with no awareness badge and nobody
   * inside on Skida has an empty strip. An empty strip is still 20 pixels and a
   * gap above the form, which on this panel is exactly what is being cleared
   * out.
   */
  if (awareness === null && !lead.hasProductUsers) return null

  return (
    <header className="ident">
      <div className="ident__text">
        <div className="ident__titlerow">
          {awareness && (
            <span className="pill" style={{ background: awareness.bg, color: awareness.color }}>
              {awareness.label}
            </span>
          )}
          {lead.hasProductUsers && (
            <span
              className="pill pill--warm"
              title="Someone at this company already has a Skida account"
            >
              {lead.productUserCount > 1
                ? `${lead.productUserCount} Skida accounts`
                : 'Skida account inside'}
            </span>
          )}
        </div>

      </div>
    </header>
  )
}
